<template>
  <div class="draft">
    <!-- IS LOCKED -->
    <div
      class="draft__is-locked"
      v-if="isLocked"
    >
      <a-spin size="small" />
      <span :style="{ marginLeft: '8px' }">
        {{ $t("synchronization.is_locked") }}
      </span>
    </div>

    <!-- IMAGES -->
    <div class="draft__images">
      <Thumbnail
        v-if="product && product.images"
        :size="44"
        class="draft__thumbnail"
        :images="product.images"
      />
      <div
        class="draft__thumbnail"
        v-else
      >
        <a-icon type="warning" />
      </div>
    </div>

    <!-- MAIN INFORMATION -->
    <div class="draft__main">
      <!-- PRODUCT AND OFFER CODES -->
      <div
        v-show="product || offer"
        class="draft__main-row"
      >
        <!-- PRODUCT CODE -->
        <div
          v-if="product"
          class="draft__code-item"
        >
          <div class="draft__code-item-name">{{ $t("synchronization.product_in_snt") }}:</div>
          <div class="draft__code-item-value">
            <ProductCode
              :code="product.p_code"
              :slug="product.slug"
            />
          </div>
        </div>

        <!-- OFFER CODE -->
        <div
          v-if="offer"
          class="draft__code-item"
        >
          <div class="draft__code-item-name">{{ $t("synchronization.offer_code") }}:</div>
          <div class="draft__code-item-value">
            <img
              :src="sntIcon"
              style="margin-right: 4px"
              alt=""
            />

            <a-popover :title="$t('synchronization.offer_properties')">
              <template slot="content">
                <DescriptionItem
                  :title="$t('synchronization.delivery_policy')"
                  :content="offer.delivery.name"
                />
                <DescriptionItem
                  :title="$t('synchronization.warranty')"
                  :content="`${offer.warranty}`"
                />
                <DescriptionItem
                  :title="$t('synchronization.celling_price')"
                  :content="`${getFormattedPrice(
                    offer.price
                  )} ${draft.currency.name.toLowerCase()}`"
                />
              </template>

              <span>
                <router-link
                  :to="{ name: 'offers', query: { search: offer.code, page: 1 } }"
                  :class="[
                    'draft__code-item-offer-value',
                    {
                      'draft__code-item-offer-value--inactive': !offer.is_active
                    }
                  ]"
                >
                  {{ offer.code }}
                </router-link>
              </span>
            </a-popover>

            <span style="margin-left: 4px">({{ stateDescription }})</span>

            <a-tooltip>
              <template slot="title">
                {{ $t("common.open_in_new_window") }}
              </template>

              <a
                style="margin-left: 8px"
                :href="`/offers?page=1&search=${offer.code}&actionAfterSearch=${true}`"
                target="_blank"
              >
                <a-icon type="select" />
              </a>
            </a-tooltip>
          </div>
        </div>
      </div>

      <!-- PRODUCT NAME -->
      <div class="draft__main-row">
        <span v-if="product">
          {{ product.name }}
        </span>
        <span
          v-else-if="hasDraftErrors"
          class="draft__product-not-selected"
        >
          {{ $t("synchronization.product_not_selected") }}
        </span>
        <span
          v-else
          class="draft__product-not-selected"
        >
          {{ $t("synchronization.match_the_goods_for_confirmation") }}
        </span>
      </div>

      <div
        class="draft__main-row"
        v-if="!product && draft.status.key === 'INVALID'"
      >
        <span>
          {{ $t("synchronization.before_to_math_fix_the") }}
          <a-popover placement="topRight">
            <span
              slot="content"
              style="max-width: 300px"
            >
              <DescriptionItem
                v-for="key in Object.keys(draft.errors)"
                :key="key"
                :title="key"
                :content="draft.errors[key]"
                style="max-width: 300px"
              />
            </span>

            <template slot="title">
              {{ $t("synchronization.draft_error") }}
            </template>

            <a @click.stop>{{ $t("synchronization.errors") }}</a>
          </a-popover>
          {{ $t("synchronization.in_xml_and_wait_synchronization") }}
        </span>
      </div>

      <!-- PRODUCT SELECTOR -->
      <div
        v-else-if="!product"
        class="draft__main-row"
      >
        <a @click.stop="$emit('changeProduct', { draftUuid, draft })">
          <a-icon :type="fetchingUpdating ? 'loading' : 'link'" />
          {{ $t("synchronization.select_product") }}
        </a>
      </div>
    </div>

    <!-- FORM -->
    <div v-show="!hasDraftErrors">
      <div
        v-if="!offer && isNeedAction"
        class="draft__main-row draft__form-wrapper"
      >
        <!-- DELIVERY -->
        <div class="draft__form-item draft__form-item--delivery">
          <a-form>
            <a-form-item
              :label="$t('synchronization.delivery_policy')"
              :validate-status="deliveryError ? 'error' : ''"
              :help="deliveryError"
            >
              <a-select
                v-model="deliveryModel"
                @change="handleChangeDelivery"
                :loading="deliveryFetching"
              >
                <a-select-option
                  v-for="politic in deliveryPoliticsWithConfigs"
                  :key="politic.uuid"
                >
                  {{ politic.name }}
                  {{ politic.shipment_city ? `(${politic.shipment_city.name})` : "" }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-form>
        </div>

        <!-- STATE -->
        <div class="draft__form-item draft__form-item--state">
          <a-form>
            <a-form-item
              :validate-status="stateError ? 'error' : ''"
              :help="stateError"
            >
              <template #label>
                {{ $t("synchronization.product_state") }}
                <!-- <a-tooltip v-if="isStateChangeBlocked">
                  <template #title>
                    {{ $t('offers.form_labels.state_tooltip') }}
                  </template>
                  <a-icon type="info-circle" theme="twoTone" style="vertical-align: -2px" />
                </a-tooltip> -->
              </template>
              <a-select
                style="width: 140px"
                v-model="stateModel"
                :loading="stateFetching"
                @change="handleChangeState"
              >
                <!-- :disabled="isStateChangeBlocked" Не працює на даний момент -->
                <!-- В SNT-6483 видалено Неробочий та Returnware стани, заблоковано видалені для сумісності -->
                <a-select-option
                  v-for="condition in conditions"
                  :key="`${condition.backKey}_${condition.value}`"
                  :value="`${condition.backKey}_${condition.value}`"
                  :disabled="condition.backKey === 'used' && condition.value < 3"
                >
                  {{ condition.description }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-form>
        </div>

        <!-- WARRANTY -->
        <div class="draft__form-item draft__form-item--warranty">
          <a-form>
            <a-form-item
              :label="$t('synchronization.warranty')"
              :validate-status="warrantyError ? 'error' : ''"
              :help="warrantyError"
            >
              <a-input-number
                style="width: 120px"
                :precision="0"
                :min="warrantyMinValue"
                v-model="warrantyModel"
                @change="handleChangeWarranty"
              />
            </a-form-item>
          </a-form>
        </div>
      </div>
    </div>

    <!-- ACTIONS -->
    <div
      class="draft__actions"
      :class="{ 'draft__actions--large': offer }"
    >
      <div
        v-if="offer"
        class="draft__button-list"
      >
        <!-- REMOVE -->
        <a-button
          class="draft__actions--deactivation"
          @click.stop="deleteOffer"
          :loading="fetchingRemoving"
          icon="delete"
        >
          {{ $t("synchronization.delete_draft") }}
        </a-button>
      </div>

      <div
        v-else-if="isNeedAction"
        class="draft__row"
      >
        <!-- CREATE -->
        <div class="draft__actions-column">
          <a-button
            @click.stop="$emit('showApproveWindow', { draft })"
            :disabled="!draft.product || draft.status.key === 'INVALID'"
            :loading="loading"
            icon="check"
          >
            {{ $t("synchronization.create") }}
          </a-button>
        </div>

        <!-- SELECT OTHER PRODUCT -->
        <div
          v-if="product"
          class="draft__actions-column"
        >
          <div class="draft__actions-column">
            <a @click.stop="$emit('changeProduct', { draftUuid, draft })">
              <a-icon :type="fetchingUpdating ? 'loading' : 'link'" />
              {{ $t("synchronization.select_other_product") }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { message } from "ant-design-vue"
import { storeToRefs } from "pinia"
import { computed, ref, watch } from "vue"

import sntIcon from "@/assets/icons/snt-icon.png"
import DescriptionItem from "@/components/Common/DescriptionItem.vue"
// Components
import ProductCode from "@/components/Common/ProductCode"
import { SearchedProduct } from "@/components/Common/SearchProducts/SearchProducts.vue"
import Thumbnail from "@/components/Common/Thumbnail"
import { conditions } from "@/consts/offerConditions"
import i18n from "@/i18n"
import { UpdateDraftPayload, useSyncDraftsStore } from "@/modules/sync/store/useSyncDraftsStore"
import { useSyncMetaStore } from "@/modules/sync/store/useSyncMetaStore"
import offersService from "@/services/offersService"
import getFormattedPrice from "@/utils/getFormattedPrice"
import getProductState from "@/utils/getProductState"

// Helper Types/Variables
const props = defineProps({
  draft: {
    type: Object,
    required: true
  },

  isNeedAction: {
    type: Boolean,
    required: true
  },

  isLocked: {
    type: Boolean,
    required: true
  },

  // Default delivery
  delivery: {
    type: String,
    required: true
  },

  warranty: {
    type: [Number, String],
    required: true
  },

  conditionValue: {
    type: String,
    required: true
  },

  synchronizationUuid: {
    type: String,
    required: true
  }
})
const emit = defineEmits(["changeProduct", "showApproveWindow", "offerDeleted"])
// End Helper Types/Variables

// Store & Route
const syncDraftsStore = useSyncDraftsStore()
const metaStore = useSyncMetaStore()

const { deliveryPolitics } = storeToRefs(metaStore)
// End Store & Route

// State
const deliveryModel = ref(props.delivery)
const stateModel = ref(props.conditionValue)
const warrantyModel = ref(props.warranty)
const fetchingRemoving = ref(false)
const fetchingUpdating = ref(false)
const warrantyFetching = ref(false)
const deliveryFetching = ref(false)
const stateFetching = ref(false)
const warrantyMinValue = ref(0)
const loading = ref(false)

// Errors
const deliveryError = ref("")
const stateError = ref("")
const warrantyError = ref("")
// End State

// Computed
const draftUuid = computed(() => props.draft.uuid)
const product = computed(() => props.draft.product)
const offer = computed(() => props.draft.offer)
const hasDraftErrors = computed(() => Object.keys(props.draft.errors).length > 0)
const deliveryPoliticsWithConfigs = computed(() =>
  deliveryPolitics.value.filter((politic: any) => politic.configs.length > 0)
)
const stateDescription = computed(() => {
  return (
    offer.value &&
    getProductState(offer.value.condition_key, offer.value.condition_value).description
  )
})
// const isStateChangeBlocked = computed(() => !!product.value && !product.value?.is_allowed_condition)
// End Computed

// Methods
// Called from refs
const productSelect = (product: SearchedProduct): void => {
  const { pCode } = product
  const payload = {
    product: pCode
  }

  fetchingUpdating.value = true
  handleUpdateDraft(payload)
    .catch(() => {
      message.error(i18n.t("synchronization.product_not_updated") as string)
    })
    .finally(() => {
      fetchingUpdating.value = false
    })
}
const checkWarranty = (value: any) => {
  if (value === "" || value === null || value === undefined) {
    return i18n.t("validations.required_field")
  }

  if (Number(value) < warrantyMinValue.value) {
    return i18n.tc("synchronization.min_num", 0)
  }

  if (String(value).length > 4) {
    return i18n.tc("validations.max", 4)
  }

  return ""
}
const handleUpdateDraft = (payload: UpdateDraftPayload) => {
  const { synchronizationUuid } = props

  return syncDraftsStore.updateDraft({
    payload,
    synchronizationUuid,
    draftUuid: draftUuid.value
  })
}
const handleChangeDelivery = (uuid: string) => {
  if (!uuid) {
    deliveryError.value = i18n.t("validations.required_field") as string
    return
  }

  deliveryError.value = ""

  deliveryFetching.value = true
  let serverErrors: any = {}

  handleUpdateDraft({ delivery: uuid })
    .catch((err: any) => {
      serverErrors = err?.response?.data || {}
      message.error(i18n.t("synchronization.delivery_not_updated") as string)
    })
    .finally(() => {
      deliveryFetching.value = false

      if (serverErrors && serverErrors.delivery) {
        deliveryError.value = serverErrors.delivery
      }
    })
}
const handleChangeState = (state: string) => {
  if (!state) {
    stateError.value = i18n.t("validations.required_field") as string
    return
  }

  stateError.value = ""

  const condition = conditions.find((item) => `${item.backKey}_${item.value}` === state)

  if (!condition) return

  const { backKey, backValue } = condition
  const payload = {
    condition_key: backKey,
    condition_value: backValue ? backValue : 1
  }

  let serverErrors: any = {}

  stateFetching.value = true
  handleUpdateDraft(payload)
    .catch((err: any) => {
      serverErrors = err?.response?.data || {}
      message.error(i18n.t("synchronization.state_not_updated") as string)
    })
    .finally(() => {
      stateFetching.value = false

      if (serverErrors && serverErrors.state) {
        stateError.value = serverErrors.state
      }
    })
}
const handleChangeWarranty = (value: number) => {
  // Validate locally first
  const error = checkWarranty(value)

  if (error) {
    warrantyError.value = error as string
    return
  }

  warrantyError.value = ""

  let serverErrors: any = {}
  warrantyFetching.value = true

  handleUpdateDraft({ warranty: value })
    .catch((err: any) => {
      serverErrors = err?.response?.data || {}
      message.error(i18n.t("synchronization.warranty_not_updated") as string)
    })
    .finally(() => {
      warrantyFetching.value = false

      if (serverErrors && serverErrors.warranty) {
        warrantyError.value = serverErrors.warranty
      }
    })
}
const deleteOffer = async () => {
  if (!offer.value || fetchingRemoving.value) return

  fetchingRemoving.value = true
  try {
    await offersService.deleteOffer(offer.value.uuid)
    emit("offerDeleted")
  } catch {
    message.error(i18n.t("synchronization.drafts_not_updated") as string)
  } finally {
    fetchingRemoving.value = false
  }
}
// End Methods

// Lifecycle
// Watchers
// Update local model if props change (though usually props -> initial value, but if external update happens...)
watch(
  () => props.delivery,
  (val) => {
    if (val !== deliveryModel.value) deliveryModel.value = val
  }
)
watch(
  () => props.conditionValue,
  (val) => {
    if (val !== stateModel.value) stateModel.value = val
  }
)
watch(
  () => props.warranty,
  (val) => {
    if (val !== warrantyModel.value) warrantyModel.value = val
  }
)

// watch(
//   isStateChangeBlocked,
//   (val) => {
//     if (val) {
//       stateModel.value = `new_1`
//     }
//   },
//   { immediate: true }
// )
// End Lifecycle

defineExpose({
  productSelect
})
</script>

<style lang="scss">
// Expanded row
.draft {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 104px;
  position: relative;

  &__is-locked {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.7);
    z-index: 10;
  }

  &__offer-deactivated {
    color: #f5222d;
    margin-left: 4px;
  }

  // Draft image
  &__images {
    margin-right: 24px;
    width: 50px;
    max-width: 50px;
    min-width: 50px;

    &::before {
      display: inline-block;
      width: 19px;
      height: calc(50% + 48px);
      position: absolute;
      top: -48px;
      left: 85px;
      background-color: transparent;
      border-bottom: 1px dashed #adadad;
      border-left: 1px dashed #adadad;
      content: "";
    }
  }

  &__thumbnail {
    padding: 3px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    width: 52px;
    height: 52px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #d9d9d9;
    font-size: 16px;
  }

  &__product-not-selected {
    color: #afafaf;
    line-height: 21px;
  }

  &__form-wrapper {
    margin-right: 24px;
  }

  // Draft main information
  &__main {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    margin-right: 24px;

    &-row {
      margin-bottom: 2px;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      font-size: 13px;
      flex: 1;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__code-item {
    margin-right: 24px;
    font-size: 13px;
    display: flex;
    align-items: center;

    &:last-child {
      margin-right: 0;
    }

    &-name {
      color: #afafaf;
      margin-right: 4px;
      display: inline-block;
    }

    &-offer-value {
      color: $brand-color-6;

      &--inactive {
        color: #afafaf;
      }
    }

    &-value {
      display: inline-flex;
      align-items: center;
    }
  }

  &__form-item {
    display: flex;
    margin-right: 20px;

    &:last-child {
      margin-right: 0;
    }

    & .ant-form-explain {
      font-size: 12px;
      margin-top: 2px;
    }

    & .ant-select-selection {
      font-size: 13px !important;
    }

    & .ant-form-item-required {
      font-size: 13px !important;
      color: #595959;

      &::before {
        opacity: 0;
        pointer-events: none;
      }
    }

    .ant-form-item-label {
      line-height: 21px;
      margin-bottom: 2px;
    }

    .ant-select {
      width: 100% !important;
    }

    .ant-form-item-control {
      line-height: 21px;
    }

    & .ant-form-item {
      margin-bottom: 0;
    }

    // &:last-child {
    //   margin-right: 0;
    // }

    &-spin {
      display: inline-block;
      width: 16px;
      margin-right: 12px;
    }

    &--delivery {
      width: 200px;

      .ant-form-horizontal {
        width: 100%;
      }
    }

    &--state {
      width: 140px;

      .ant-form-horizontal {
        width: 100%;
      }
    }

    &--warranty {
      & .ant-form-item-control-wrapper {
        width: 100% !important;
      }
    }
  }

  &__button-list {
    display: flex;
    flex: 1;
    justify-content: space-between;
  }

  // Draft actions area
  &__actions {
    min-width: 242px;
    padding: 0 0 0 8px;
    display: flex;
    justify-content: flex-start;
    flex-direction: row;

    &--large {
      padding-left: 0;
      min-width: 385px;
    }

    &--deactivation {
      &:hover {
        color: #f5222d;
        border: 1px solid #f5222d;
      }
    }

    &-row {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
    }

    &-column {
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
